/**
 * Generating an EXTERNALLY-SOURCED set's two documents from a form — ADR-135 D5, S8.
 *
 * WHAT MAKES THIS DIFFERENT FROM `schemaDraft.ts`
 * ---------------------------------------------
 * Both emit a schema plus a `config/data/<set>.v<N>.json`, and there the resemblance stops. A
 * stored set's data document holds RECORDS. An external set's holds the CONTRACT and no values
 * at all (ADR-131 D2, mechanical in S8 E3): `$schema_ref`, `owner`, `source`, `freshness`, and
 * nothing else. Emitting an empty collection here — the natural instinct, and what the stored
 * generator correctly does — would be refused by the canonical validator as
 * `contract_carries_values`.
 *
 * The schema is still emitted, and it is still required. It is the D4 SNAPSHOT: the shape a
 * resolve is validated against at resolution time, what ADR-131 D12 checks cross-set integrity
 * against, and what a drift re-derivation compares to. What changes is only WHEN the instance
 * exists — at resolution, never in git.
 *
 * WHY THE FIELD LIST IS TYPED AND NOT PICKED
 * -----------------------------------------
 * ADR-135 D5, the owner ruling: degraded authoring ships FIRST. The picker needs
 * `POST /internal/data-resolve` (D1), which is not built. Execution was never blocked — a fleet
 * agent resolves through its own MCP server at runtime today — so only the author-time
 * pick-list is missing, and shipping the editor without it is strictly better than shipping
 * nothing until the endpoint lands.
 *
 * The ENTITY is typed for a second and unrelated reason: `management.data` (S1's home for both
 * discovery tiers) is not in the platform manifest yet. So there is no declaration to pick from,
 * which by S8 E6 also makes every `mcp:` set mode `asserted`. Two degradations, two causes, and
 * the UI names them separately because they clear at different times.
 *
 * WHAT IT DELIBERATELY WILL NOT EMIT
 * ---------------------------------
 *  1. `resolved_id` / `resolved_at` — S8 E8. Resolution stamps both, together; the form emits
 *     `instance: {name}` alone, which is the clean starting state the validator accepts. An id
 *     an operator typed is a hardcoded GID wearing a cache's name.
 *  2. `shared_instance: true` — S2 routes a shared-instance projection to the CORE lane, which
 *     this surface cannot write to (`registryPathOK` refuses `config/data/org/**`), and whose
 *     PR is opened by `core-advisor` → `hermes-propose --core`, not by an MC endpoint. Offering
 *     the toggle would be offering a choice that cannot be saved — the same reason
 *     `schemaDraft` has no owner picker. V1 has no consumer for it either: Asana's instances are
 *     per-fleet, and HubSpot is out of scope until its org-scoped credential exists.
 *  3. A verdict. That is the canonical Python validator's answer, via
 *     `POST /api/data-references/validate-draft` staged over the real registry. Every S8 rule —
 *     `contract_carries_values`, `provider_invalid`, `entity_missing`, `projection_key_missing`,
 *     `write_vocabulary_in_projection`, `resolved_id_unattested`, `freshness_missing` — is
 *     enforced there. Re-implementing any of them here is the drift ADR-129 D1 exists to
 *     prevent, so this module produces a well-FORMED candidate and refuses only the inputs that
 *     cannot become one.
 */

export type SourceClass = 'mcp' | 'http'

/** One entity a provider declares in `management.data` (S1 §1). */
export interface SourceEntity {
  entity: string
  label: string
  /** Whether it declares a FIELD tier (D13 tier 2) — what S8 E6 derives the mode from, and
   *  per ENTITY because one provider holds both: Asana's `task` is introspectable (custom
   *  fields vary per project and Asana publishes them) while its `section` is not (Asana
   *  describes no section shape), which is S1 F4's first-class `unsupported`. */
  fields: boolean
  /** Whether it declares an INSTANCE tier (D13 tier 1). Reported separately from `fields`
   *  because the two are independent: Asana's `section` resolves instances and describes no
   *  shape, so one flag would have to lie about one of them. */
  instance?: boolean
  /** What the field tier needs SUPPLIED before it can be resolved (ADR-135 4a binds a tier's
   *  scope from the caller's args at author time). Present and empty means "nothing" — an
   *  answer the picker acts on by resolving immediately. */
  fields_scope?: SourceScopeParam[]
}

/** One parameter a discovery tier is scoped by (S1). `from` chooses the control, and it is the
 *  only thing that does: `instance` resolves the instance tier into a pick-list, `enum` offers
 *  `values` directly. Anything else has no control, and the editor says so rather than guessing.
 *
 *  This is read from the platform manifest, never assumed, because the shipped providers differ:
 *  Asana's `task.fields` is scoped by `project_gid` from an instance, HubSpot's
 *  `crm_object.fields` by `object_type` from a closed vocabulary. */
export interface SourceScopeParam {
  name: string
  from: string
  /** The closed vocabulary, for `from: 'enum'` only. */
  values?: string[]
}

export interface SourceProvider {
  provider: string
  class: SourceClass
  name: string
  /** Empty for an `http:` provider by design — S3's column spec answers the field question
   *  with no resolver at all, so it is not missing a tier. */
  entities: SourceEntity[]
}

/** One projected field: our schema's field name, and the path it reads in the provider's shape. */
export interface MappedField {
  name: string
  /** The provider's path — `custom_field.name`, `gid`. Typed, not picked (see the module note). */
  from: string
  type: 'string' | 'number' | 'integer' | 'boolean'
  required: boolean
}

export interface SourceDraftSpec {
  /** The SCHEMA name, without the version. */
  name: string
  /** The SET name — the data file's stem, which is what `<set>@v<N>` becomes. */
  setName: string
  version: number
  description: string
  /** `mcp:<server>` | `http:<name>`, from the served catalog. */
  provider: string
  /** The entity in `management.data`. Required for `mcp:`; an `http:` provider has none. */
  entity: string
  /** The instance's NAME. Authoritative; the id is a cache resolution writes (S5 N1, S8 E8). */
  instanceName: string
  /** The collection property the resolved records land in. */
  collection: string
  /** The STABLE identifier we store and reference — a name, never a volatile id (D5, D11). */
  key: string
  /** The runtime blast-radius cap. Mandatory, and there is no platform ceiling (S8 E7). */
  maxItems: number
  /** Minutes. The KEY is required by S8; its interior belongs to ADR-131 D7 (see below). */
  ttlMinutes: number
  fields: MappedField[]
}

export interface GeneratedSourceDraft {
  schemaPath: string
  dataPath: string
  schema: Record<string, unknown>
  data: Record<string, unknown>
}

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/
const PROVIDER = /^(mcp|http):[a-z0-9][a-z0-9-]*$/
/** S1 F5 / ADR-131 D5: a projection carries no write vocabulary, anywhere. Refused on the field
 *  NAME too, not only inside a mapping — `writable` exists on a field DEFINITION (a discovery
 *  result) and never on a stored mapping, so it cannot be read back as authorisation. */
const WRITE_VOCABULARY = ['direction', 'read_write', 'writable']

export function classOf(provider: string): SourceClass | null {
  if (provider.startsWith('mcp:')) return 'mcp'
  if (provider.startsWith('http:')) return 'http'
  return null
}

/**
 * Refusals, in the order an operator would hit them — each one a draft that cannot be made
 * well-formed, as distinct from one the canonical validator might reject for a deeper reason.
 */
export function sourceProblems(spec: SourceDraftSpec): string[] {
  const problems: string[] = []
  if (!SLUG.test(spec.name)) {
    problems.push('The schema name must be a lowercase slug, e.g. asana-triage-section.')
  }
  if (!SLUG.test(spec.setName)) {
    problems.push('The set name must be a lowercase slug, e.g. asana-triage-sections.')
  }
  if (!Number.isInteger(spec.version) || spec.version < 1) {
    problems.push('The version must be a whole number, 1 or greater.')
  }
  if (!PROVIDER.test(spec.provider)) {
    problems.push(
      'Choose a provider. It is a class and instance — mcp:<server> or http:<name> — never a ' +
      'URL or an endpoint: the transport is looked up from the declaration.')
  }
  // An `http:` provider answers the field question with its own column spec (S3), so it has no
  // entity tier and requiring one would invent a field for the one class that needs less wiring.
  if (classOf(spec.provider) === 'mcp' && !spec.entity.trim()) {
    problems.push('An mcp: source names the entity its resolvers are declared against.')
  }
  if (!/^[a-z0-9_]+$/.test(spec.collection)) {
    problems.push('The collection name must be lowercase letters, digits or underscores.')
  }
  if (spec.fields.length === 0) {
    problems.push('A projection with no fields projects nothing.')
  }
  const names = spec.fields.map(f => f.name)
  if (new Set(names).size !== names.length) problems.push('Two fields share a name.')
  for (const f of spec.fields) {
    if (!f.name.trim()) problems.push('A field has no name.')
    else if (!f.from.trim()) {
      problems.push(`"${f.name}" has no source path — name the path it reads in the provider.`)
    }
    if (WRITE_VOCABULARY.includes(f.name)) {
      problems.push(
        `"${f.name}" cannot be a projected field: a projection is read-only by construction, ` +
        `and reading a value never implies the write is its inverse. A write is a separately ` +
        `declared action with its own tier, precondition and idempotency key.`)
    }
  }
  if (!spec.key.trim()) {
    problems.push('Name the stable key — what consumers spell as by_key.<value>.')
  } else if (!names.includes(spec.key)) {
    problems.push(`The key "${spec.key}" is not one of this projection's fields.`)
  }
  if (!Number.isInteger(spec.maxItems) || spec.maxItems < 1) {
    // Mandatory and uncapped (S8 E7). The form refuses only a MISSING or nonsensical bound; a
    // large one is legitimate, and lowering it on sight turns a working resolve into a runtime
    // oversize failure rather than making the provider return fewer rows.
    problems.push('Declare the maximum number of records — it is the runtime cap, and it may ' +
      'be as large as the source genuinely returns.')
  }
  if (!Number.isInteger(spec.ttlMinutes) || spec.ttlMinutes < 1) {
    problems.push('Declare a freshness TTL. An undeclared one is how a value that looks ' +
      'current silently is not.')
  }
  return problems
}

/** The entities a provider declares, or [] when it declares none / is unknown. */
export function entitiesFor(
  provider: string, providers: SourceProvider[] | null,
): SourceEntity[] {
  return providers?.find(p => p.provider === provider)?.entities ?? []
}

/**
 * The derivation mode, DERIVED and never stored (S8 E6).
 *
 * A function of facts already declared, so it cannot disagree with them: a stored `asserted`
 * would still read `asserted` the day the provider gains a field resolver, and the drift job
 * would keep skipping a set it could now check.
 *
 * Keyed on the ENTITY, not the provider, because one provider holds both — Asana's `task`
 * declares a field tier and its `section` deliberately does not.
 */
export function derivationMode(
  provider: string, entity: string, providers: SourceProvider[] | null,
): 'declared' | 'introspected' | 'asserted' {
  if (classOf(provider) === 'http') return 'declared'
  const declared = entitiesFor(provider, providers).find(e => e.entity === entity)
  // Unknown entity -> asserted, deliberately. Claiming a set is checkable against a field
  // tier we cannot find is the failure this distinction exists to prevent, and it is also the
  // honest answer while an operator is still typing an entity name.
  return declared?.fields ? 'introspected' : 'asserted'
}

/** What the FIELD tier of one entity needs supplied (ADR-135 4a).
 *
 *  Returns `[]` for an entity that needs nothing, and `[]` for an entity with no field tier at
 *  all — the caller has already branched on `fields` before it gets here, and conflating the two
 *  in this function would put that branch in two places.
 */
export function fieldScopeFor(
  provider: string, entity: string, providers: SourceProvider[] | null,
): SourceScopeParam[] {
  return entitiesFor(provider, providers).find(e => e.entity === entity)?.fields_scope ?? []
}

/** Whether the instance tier can be resolved for this entity — the precondition for offering an
 *  instance pick-list for a `from: 'instance'` scope parameter. */
export function hasInstanceTier(
  provider: string, entity: string, providers: SourceProvider[] | null,
): boolean {
  return entitiesFor(provider, providers).find(e => e.entity === entity)?.instance === true
}

/**
 * A provider's NORMALISED type, mapped into the type a schema field may declare — or a refusal.
 *
 * The gateway already normalises the provider's own type name (`multi_enum` -> `enum_multi`), so
 * this maps one closed vocabulary onto another and has no provider-specific knowledge in it.
 *
 * WHY IT REFUSES INSTEAD OF FALLING BACK TO `string`
 * -------------------------------------------------
 * Two normalised types have no scalar equivalent. `enum_multi` is a LIST, and mapping it to
 * `string` would declare a contract that flattens the provider's several values into one — a
 * misdeclaration the validator cannot catch, because a string field holding a joined list is
 * perfectly valid JSON. `unsupported` is the gateway saying it could not describe the field at
 * all, and inventing a type for it is exactly the "could not look" -> "found this" collapse this
 * programme refuses. Both stay pickable-but-refused with the reason shown; the hand-typed row
 * below remains the escape hatch (ADR-121 D1), and it is the operator's assertion there.
 *
 * `integer` is absent on purpose: no provider reports it, and the table still offers it for a
 * hand-typed row, so nothing is lost by not guessing it here.
 */
export function schemaTypeFor(
  providerType?: string,
): { type: MappedField['type'] } | { refuse: string } {
  switch (providerType) {
    case 'number':
      return { type: 'number' }
    case 'boolean':
      return { type: 'boolean' }
    // A date arrives as a string and this contract does not enforce its format — said in the UI
    // rather than silently, so nobody reads `string` as "validated as a date".
    case 'date':
    case 'datetime':
    case 'enum':
    case 'reference':
    case 'string':
    case undefined:
    case '':
      return { type: 'string' }
    case 'enum_multi':
      return { refuse: 'multi-valued — this contract stores one value per field, so mapping it ' +
        'would flatten the provider’s several values into one' }
    case 'unsupported':
      return { refuse: 'the provider does not describe this field’s type, so there is ' +
        'nothing to map it to' }
    default:
      // A normalised type this frontend has not been taught. Refused rather than defaulted: the
      // gateway's vocabulary grew and this mapping is what has to be updated.
      return { refuse: `unrecognised type ${providerType} — this editor has no mapping for it` }
  }
}

function fieldSchema(f: MappedField): Record<string, unknown> {
  return { type: f.type }
}

/** The D4 snapshot schema plus the CONTRACT document, ready to propose as one changeset. */
export function generateSourceDraft(spec: SourceDraftSpec): GeneratedSourceDraft {
  const record: Record<string, unknown> = {}
  for (const f of spec.fields) record[f.name] = fieldSchema(f)

  const schema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: `${spec.name}@v${spec.version}`,
    title: spec.name,
    description: spec.description,
    status: 'published',
    // Fleet-owned, with no picker: S2 routes a shared-instance (core-owned) projection through
    // the core lane, which this surface cannot write to. See the module note.
    owner: 'fleet',
    type: 'object',
    properties: {
      [spec.collection]: {
        type: 'array',
        // Mandatory on every array in every schema and always has been
        // (`check_bounded_collections`); S8 E7 adds only that a large value is legitimate.
        maxItems: spec.maxItems,
        'x-key': spec.key,
        items: {
          type: 'object',
          properties: record,
          required: spec.fields.filter(f => f.required).map(f => f.name),
        },
      },
    },
    required: [spec.collection],
  }

  const fields: Record<string, unknown> = {}
  for (const f of spec.fields) fields[f.name] = { from: f.from }

  const source: Record<string, unknown> = {
    provider: spec.provider,
    key: spec.key,
    fields,
  }
  if (classOf(spec.provider) === 'mcp') {
    source.entity = spec.entity.trim()
    if (spec.instanceName.trim()) {
      // NAME only. Resolution stamps `resolved_id` and `resolved_at` together — S8 E8 — and an
      // operator-typed id would be refused as unattested.
      source.instance = { name: spec.instanceName.trim() }
    }
  }

  return {
    schemaPath: `config/schemas/${spec.name}/v${spec.version}.json`,
    dataPath: `config/data/${spec.setName}.v${spec.version}.json`,
    schema,
    data: {
      $schema_ref: `${spec.name}@v${spec.version}`,
      owner: 'fleet',
      source,
      // S8 requires the KEY; ADR-131 D7 owns the interior, and this is the one place the form
      // is ahead of a written grammar. `ttl_minutes` is the spelling S8's own examples use; if
      // D7 lands a different one this is a one-line change, and the validator checks only that
      // the key is present — so a mismatch cannot ship a document that silently means nothing.
      freshness: { ttl_minutes: spec.ttlMinutes },
    },
  }
}
